// client/src/coms/views/canvas_parts/useSketchRelayGame.js
// Client side of Sketch Relay. The server owns the game; this hook mirrors its state from the canvas live stream
// and sends player actions. Nothing here decides rules.
import { useCallback, useEffect, useRef, useState } from 'react';

const ERROR_MESSAGES = {
  not_enough_players: 'At least two connected players are needed.',
  owner_only: 'Only the canvas owner can do that.',
  game_in_progress: 'A game is already running.',
  game_not_finished: 'The game has not finished yet.',
  no_game_running: 'No game is running.',
  room_full: 'This game is full.',
  not_a_player: 'Join the game first.',
  drawer_cannot_guess: 'The drawer cannot guess.',
  already_guessed: 'You already guessed it.',
  stale_turn: 'That turn has already ended.',
  no_turn: 'Nobody is drawing right now.',
  empty_guess: 'Type a guess first.',
  guess_too_long: 'That guess is too long.',
  slow_down: 'Slow down a little.',
  empty_category: 'That category has no prompts.',
  forbidden: 'You do not have access to this canvas.',
  not_authenticated: 'Sign in to play.',
};

export function describeSketchRelayError(code) {
  return ERROR_MESSAGES[code] || 'Something went wrong. Try again.';
}

function createActionId() {
  return `a-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Newer state wins; a different roomId means the server recreated the room (for example after a restart).
export function isNewerSketchRelayState(current, next) {
  if (!next || typeof next !== 'object') return false;
  if (!current) return true;
  if (next.roomId !== current.roomId) return true;
  return Number(next.rev) >= Number(current.rev);
}

export default function useSketchRelayGame({ canvasId, enabled = true }) {
  const safeCanvasId = Number(canvasId);
  const active = enabled && Number.isInteger(safeCanvasId) && safeCanvasId > 0;
  const [state, setState] = useState(null);
  const [privateInfo, setPrivateInfo] = useState({ prompt: null });
  const [feedback, setFeedback] = useState(null);
  const [pendingAction, setPendingAction] = useState('');
  const [loadError, setLoadError] = useState('');
  const stateRef = useRef(null);
  const clockOffsetRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const applyState = useCallback((next) => {
    if (!next || Number(next.canvasId) !== safeCanvasId) return false;
    if (!isNewerSketchRelayState(stateRef.current, next)) return false;
    stateRef.current = next;
    if (Number.isFinite(Number(next.serverNow))) clockOffsetRef.current = Number(next.serverNow) - Date.now();
    setState(next);
    return true;
  }, [safeCanvasId]);

  const applyPrivate = useCallback((next) => {
    setPrivateInfo(next && typeof next === 'object' ? { prompt: next.prompt || null } : { prompt: null });
  }, []);

  const refresh = useCallback(async () => {
    if (!active) return;
    try {
      const response = await fetch(`/api/canvas-live/${encodeURIComponent(String(safeCanvasId))}/game`, {
        credentials: 'include',
        cache: 'no-store',
      });
      const data = await response.json().catch(() => ({}));
      if (!mountedRef.current) return;
      if (!response.ok) {
        setLoadError(describeSketchRelayError(data && data.error));
        return;
      }
      setLoadError('');
      applyState(data.state);
      applyPrivate(data.private);
    } catch {
      if (mountedRef.current) setLoadError('Could not reach the game server.');
    }
  }, [active, applyPrivate, applyState, safeCanvasId]);

  useEffect(() => {
    stateRef.current = null;
    setState(null);
    setPrivateInfo({ prompt: null });
    refresh();
  }, [refresh]);

  // If a deadline passes without an update (for example a dropped live stream), ask the server directly.
  useEffect(() => {
    if (!active || !state) return undefined;
    const deadline = state.status === 'drawing' && state.turn
      ? Number(state.turn.endsAt)
      : state.status === 'reveal' && state.reveal
        ? Number(state.reveal.endsAt)
        : 0;
    if (!deadline) return undefined;
    const wait = Math.max(1000, deadline - (Date.now() + clockOffsetRef.current) + 2500);
    const timer = window.setTimeout(refresh, wait);
    return () => window.clearTimeout(timer);
  }, [active, refresh, state]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = window.setTimeout(() => setFeedback(null), 3500);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  // Called by the canvas live stream handler for every event; returns true when the event was a game event.
  const handleLiveEvent = useCallback((event) => {
    if (!event || typeof event !== 'object') return false;
    if (event.type === 'game_state') {
      applyState(event.state);
      return true;
    }
    if (event.type === 'game_private') {
      applyPrivate(event.private);
      return true;
    }
    return false;
  }, [applyPrivate, applyState]);

  const act = useCallback(async (action, payload = {}) => {
    if (!active) return null;
    setPendingAction(action);
    try {
      const response = await fetch(`/api/canvas-live/${encodeURIComponent(String(safeCanvasId))}/game`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, action, actionId: createActionId() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!mountedRef.current) return data;
      if (data && data.state) applyState(data.state);
      if (data && data.private) applyPrivate(data.private);
      if (!response.ok) {
        setFeedback({ kind: 'error', text: describeSketchRelayError(data && data.error), at: Date.now() });
      } else if (action === 'guess' && data.verdict === 'close') {
        setFeedback({ kind: 'close', text: 'So close! Keep trying.', at: Date.now() });
      } else if (action === 'guess' && data.verdict === 'correct') {
        setFeedback({ kind: 'correct', text: `You got it! +${data.points || 0}`, at: Date.now() });
      }
      return { ok: response.ok, ...data };
    } catch {
      if (mountedRef.current) setFeedback({ kind: 'error', text: 'Could not reach the game server.', at: Date.now() });
      return null;
    } finally {
      if (mountedRef.current) setPendingAction('');
    }
  }, [active, applyPrivate, applyState, safeCanvasId]);

  const serverNow = useCallback(() => Date.now() + clockOffsetRef.current, []);

  return {
    state,
    privateInfo,
    feedback,
    pendingAction,
    loadError,
    act,
    refresh,
    handleLiveEvent,
    serverNow,
  };
}
